import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN || '';
const IS_BROWSER = typeof window !== 'undefined';

let initialized = false;

export function initMixpanel() {
  if (!IS_BROWSER || initialized || !MIXPANEL_TOKEN) return;
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: process.env.NODE_ENV === 'development',
    track_pageview: true,
    persistence: 'localStorage',
    ignore_dnt: false,
  });
  initialized = true;
}

function track(event: string, properties?: Record<string, unknown>) {
  if (!IS_BROWSER) return;
  try {
    initMixpanel();
    mixpanel.track(event, properties);
  } catch {}
}

// ─── Identity ────────────────────────────────────────────────────────────────

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!IS_BROWSER) return;
  try {
    initMixpanel();
    mixpanel.identify(userId);
    if (traits) {
      mixpanel.people.set({
        $email: traits.email,
        $name: traits.name,
        plan: traits.plan,
        profession: traits.profession,
        ...traits,
      });
    }
  } catch {}
}

export function resetUser() {
  if (!IS_BROWSER) return;
  try {
    initMixpanel();
    mixpanel.reset();
  } catch {}
}

// ─── Conversion Funnel ───────────────────────────────────────────────────────

/** Step 1 – User lands on sign-up page */
export function trackSignupPageViewed() {
  track('Signup Page Viewed');
}

/** Step 2 – User submits the signup form */
export function trackSignupStarted(properties: { email: string }) {
  track('Signup Started', { email: properties.email });
}

/** Step 3 – Signup succeeded */
export function trackSignupCompleted(properties: { userId: string; email: string; method?: string }) {
  track('Signup Completed', {
    user_id: properties.userId,
    email: properties.email,
    method: properties.method || 'email',
  });
  try {
    initMixpanel();
    mixpanel.alias(properties.userId);
  } catch {}
}

/** Step 4 – User logs in */
export function trackLogin(properties: { userId: string; email: string }) {
  track('User Logged In', {
    user_id: properties.userId,
    email: properties.email,
  });
}

/** Step 5 – Onboarding completed */
export function trackOnboardingCompleted(properties: {
  profession: string;
  goals: string[];
  platforms: string[];
}) {
  track('Onboarding Completed', {
    profession: properties.profession,
    goals: properties.goals,
    platforms: properties.platforms,
    goals_count: properties.goals.length,
    platforms_count: properties.platforms.length,
  });
}

/** Step 6 – User views pricing page */
export function trackPricingPageViewed(properties?: { source?: string }) {
  track('Pricing Page Viewed', { source: properties?.source });
}

/** Step 7 – User toggles billing cycle */
export function trackBillingToggled(properties: { cycle: 'monthly' | 'annual' }) {
  track('Billing Cycle Toggled', { billing_cycle: properties.cycle });
}

/** Step 8 – User clicks a plan CTA */
export function trackPlanSelected(properties: {
  plan: string;
  billing_cycle: 'monthly' | 'annual';
  currency: string;
  price: number;
}) {
  track('Plan Selected', {
    plan_name: properties.plan,
    billing_cycle: properties.billing_cycle,
    currency: properties.currency,
    price: properties.price,
  });
}

/** Step 9 – Checkout initiated (Razorpay order created) */
export function trackCheckoutInitiated(properties: {
  plan: string;
  billing_cycle: 'monthly' | 'annual';
  currency: string;
  amount: number;
  order_id: string;
}) {
  track('Checkout Initiated', {
    plan_name: properties.plan,
    billing_cycle: properties.billing_cycle,
    currency: properties.currency,
    amount: properties.amount,
    order_id: properties.order_id,
  });
}

/** Step 10 – Payment succeeded */
export function trackCheckoutCompleted(properties: {
  plan: string;
  billing_cycle: 'monthly' | 'annual';
  currency: string;
  amount: number;
  order_id: string;
  payment_id: string;
}) {
  track('Checkout Completed', {
    plan_name: properties.plan,
    billing_cycle: properties.billing_cycle,
    currency: properties.currency,
    amount: properties.amount,
    order_id: properties.order_id,
    payment_id: properties.payment_id,
  });
  // Update people profile with subscription
  try {
    initMixpanel();
    mixpanel.people.set({
      plan: properties.plan,
      billing_cycle: properties.billing_cycle,
      subscription_status: 'active',
    });
  } catch {}
}

/** Payment failed */
export function trackCheckoutFailed(properties: {
  plan: string;
  billing_cycle: 'monthly' | 'annual';
  reason?: string;
}) {
  track('Checkout Failed', {
    plan_name: properties.plan,
    billing_cycle: properties.billing_cycle,
    failure_reason: properties.reason,
  });
}

/** Checkout modal dismissed */
export function trackCheckoutAbandoned(properties: { plan: string }) {
  track('Checkout Abandoned', { plan_name: properties.plan });
}

// ─── Studio Adoption ─────────────────────────────────────────────────────────

export type StudioName =
  | 'Video Studio' |'Audio Studio' |'AI Writing Studio' |'AI Image Generation Studio' |'Graphic Design Editor' |'eBook Studio' |'Carousel Creator' |'Content Calendar' |'Asset Library';

export function trackStudioOpened(studio: StudioName) {
  track('Studio Opened', { studio_name: studio });
}

export function trackStudioFeatureUsed(properties: {
  studio: StudioName;
  feature: string;
  details?: Record<string, unknown>;
}) {
  track('Studio Feature Used', {
    studio_name: properties.studio,
    feature_name: properties.feature,
    ...properties.details,
  });
}

export function trackContentGenerated(properties: {
  studio: StudioName;
  content_type: string;
  details?: Record<string, unknown>;
}) {
  track('Content Generated', {
    studio_name: properties.studio,
    content_type: properties.content_type,
    ...properties.details,
  });
}

export function trackExportCompleted(properties: {
  studio: StudioName;
  format: string;
  details?: Record<string, unknown>;
}) {
  track('Export Completed', {
    studio_name: properties.studio,
    export_format: properties.format,
    ...properties.details,
  });
}
